import { useId } from 'react';
import { Badge } from '@/components/Badge';
import ActionButton from '@/components/Button/ActionButton';
import { Checkbox } from '@/components/Checkbox';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { Separator } from '@/components/ui/separator';
import type { JobTypeValue } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
import { MasterDataCombobox } from './MasterDataCombobox';
type Props = CFormItemProps & {
  value?: JobTypeValue[];
  onChange: (value: JobTypeValue[]) => void;
};
export const CFormJobTypeListItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const idPrefix = useId();
  const values = props.value || [];
  const handleAdd = () => {
    props.onChange([...values, {
      jobTypeId: '',
      name: '',
      isPrimary: values.length === 0
    }]);
  };
  const handleRemove = (index: number) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    props.onChange(newValues);
  };
  const handleChange = (index: number, field: 'jobTypeId' | 'isPrimary', value: string | boolean, label?: string) => {
    let newValues = values.map(v => ({
      ...v
    }));
    if (field === 'isPrimary' && value === true) {
      newValues = newValues.map((v, i) => ({
        ...v,
        isPrimary: i === index
      }));
    } else if (field === 'jobTypeId') {
      newValues[index] = {
        ...newValues[index],
        jobTypeId: value as string,
        name: label || ''
      };
    }
    props.onChange(newValues);
  };
  const renderReadMode = () => {
    if (values.length === 0) return <Text variant="p">-</Text>;
    return <VStack className="gap-2 w-full">
        {values.map((item, index) => <VStack key={`${idPrefix}-${index}`} className="gap-2 w-full">
            <HStack className="items-center gap-2">
              <Text variant="labelLarge">{item.name || '-'}</Text>
              {item.isPrimary && <Badge variant="secondary">{tc('primary')}</Badge>}
            </HStack>
          </VStack>)}
      </VStack>;
  };
  const renderWriteMode = () => {
    return <VStack className="gap-2 w-full">
        {values.length > 0 && <>
            <HStack className="items-center w-full gap-4 ml-1">
              <div className="flex-1 justify-start">
                <Text variant="tableHead">{props.label}</Text>
              </div>
              <div className="w-[80px] justify-center flex">
                <Text variant="tableHead">{tc('primary')}</Text>
              </div>
              <div className="w-10"></div>
            </HStack>
            <Separator className="w-full" />
          </>}

        {values.map((item, index) => <VStack key={`${idPrefix}-${index}`} className="gap-2 w-full">
            <HStack className="items-center w-full gap-4 ml-1">
              <div className="flex-1 min-w-0">
                <MasterDataCombobox value={item.jobTypeId ?? undefined} displayValue={item.name ?? undefined} onChange={(id, name) => handleChange(index, 'jobTypeId', id || '', name)} apiPath="/v2/job-types" queryKeyPrefix="jobTypes" disabled={props.readonly} />
              </div>
              <div className="w-[80px] justify-center flex">
                <Checkbox checked={item.isPrimary ?? false} onCheckedChange={checked => {
              if (!checked) return;
              handleChange(index, 'isPrimary', true);
            }} disabled={props.readonly} />
              </div>
              <div className="w-10">
                <ActionButton icon="Trash2Icon" variant="ghost" iconColor="muted" onClick={() => handleRemove(index)} disabled={props.readonly || props.required !== false && (item.isPrimary ?? false)} aria-label={tc('action.delete')} />
              </div>
            </HStack>
            <Separator className="w-full" />
          </VStack>)}

        {!props.readonly && <HStack className="items-left">
            <ActionButton icon="PlusIcon" onClick={handleAdd} variant="ghost" iconColor="primary">
              {tc('action.add')}
            </ActionButton>
          </HStack>}
      </VStack>;
  };
  return <CFormItemBase {...props} readComponent={renderReadMode()} writeComponent={renderWriteMode()} />;
});